import { useMemo, useState, useCallback } from 'react';
import Loading from '../../components/globales/Loading';
import TablaLayout from '../../components/globales/TablaLayout';
import ModalDynamic from '../../components/globales/ModalDynamic';
import useCrud from '../../hooks/useCrud';
import ENDPOINTS from '../../api/endpoints';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function Manifiestos() {
    const keyField = 'id';
    const ITEMS_PER_PAGE = 15;

    const onroadEndpoints = useMemo(() => ({
        listar: ENDPOINTS.onroad.listar,
        consultar: ENDPOINTS.onroad.consultar,
        borrar: ENDPOINTS.onroad.borrar
    }), []);

    const formatearFecha = (fecha) => {
        if (!fecha) return '-';
        return new Date(fecha).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderEstado = (estado) => (
        <span className={`badge ${estado ? 'bg-success' : 'bg-danger'}`}>{estado ? 'Activo' : 'Inactivo'}</span>
    );

    const renderBooleano = (valor) => (
        <span className={`badge ${valor ? 'bg-primary' : 'bg-secondary'}`}>{valor ? 'Sí' : 'No'}</span>
    );

    const tableColumns = [
        { label: 'No', key: 'index', align: 'center', render: (_, i) => i + 1 },
        { label: 'Manifiesto', key: 'manifiesto' },
        { label: 'Placa', key: 'placa' },
        { label: 'Conductor', key: 'nombre_conductor', render: (value) => value || '-' },
        { label: 'Empresa', key: 'nombre_empresa' },
        { label: 'Estado', key: 'estado', align: 'center', render: (value) => renderEstado(value) },
        { label: 'Salida', key: 'salida', align: 'center', render: (value) => renderBooleano(value) },
        { label: 'Llegada', key: 'llegada', align: 'center', render: (value) => renderBooleano(value) }
    ];

    const modalColumns = [
        { label: 'ID', key: 'id', readonly: true },
        { label: 'Código Referencia', key: 'codigo_referencia', readonly: true },
        { label: 'Manifiesto', key: 'manifiesto', readonly: true },
        { label: 'Placa Vehículo', key: 'placa', readonly: true },
        { label: 'Documento Conductor', key: 'conductor', readonly: true },
        { label: 'Nombre Conductor', key: 'nombre_conductor', readonly: true },
        { label: 'Ruta de Viaje', key: 'ruta_viaje', readonly: true, as: 'textarea' },
        { label: 'Empresa', key: 'nombre_empresa', readonly: true },
        { label: 'Código Empresa', key: 'codigo_empresa', readonly: true },
        {
            label: 'Estado',
            key: 'estado',
            readonly: true,
            render: (value, _, __, accion) => (accion === 'consultar' ? renderEstado(value) : (value ? 'Activo' : 'Inactivo'))
        },
        {
            label: 'Salida Realizada',
            key: 'salida',
            readonly: true,
            render: (value, _, __, accion) => (accion === 'consultar' ? renderBooleano(value) : (value ? 'Sí' : 'No'))
        },
        {
            label: 'Llegada Realizada',
            key: 'llegada',
            readonly: true,
            render: (value, _, __, accion) => (accion === 'consultar' ? renderBooleano(value) : (value ? 'Sí' : 'No'))
        },
        { label: 'URL Empresa', key: 'url', readonly: true },
        { label: 'ID Empresa Código', key: 'id_empresa_codigo', readonly: true },
        { label: 'Fecha Creación', key: 'created_at', readonly: true, render: (value) => formatearFecha(value) },
        { label: 'Última Actualización', key: 'updated_at', readonly: true, render: (value) => formatearFecha(value) }
    ];

    const initForm = () => {
        const obj = {};
        modalColumns.forEach(col => {
            if (col.key !== 'index') obj[col.key] = '';
        });
        return obj;
    };

    const {
        data,
        loading,
        error,
        showModal,
        actionType,
        formData,
        errors,
        loadingModal,
        openModal,
        closeModal,
        handleChange,
        submit,
        remove,
    } = useCrud(onroadEndpoints, {
        keyField,
        initForm,
        columns: modalColumns,
        schema: null
    });

    // ===================== FILTRO POR EMPRESA =====================
    const [companyFilter, setCompanyFilter] = useState('');
    const companies = useMemo(() => {
        const set = new Set();
        (data || []).forEach(d => {
            if (d && d.nombre_empresa) set.add(String(d.nombre_empresa));
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }, [data]);

    const filteredData = useMemo(() => {
        if (!companyFilter) return data;
        return data.filter(d => String(d.nombre_empresa) === String(companyFilter));
    }, [data, companyFilter]);

    const handleCompanyChange = (e) => {
        const value = e.target.value;
        setCompanyFilter(value);
        setPage(1);
        setHighlightValue(null);
        try {
            const url = new URL(window.location.href);
            if (value) url.searchParams.set('empresa', value);
            else url.searchParams.delete('empresa');
            window.history.replaceState({}, '', url.toString());
        } catch { /* Ignorar el error - La actualización de la URL es sólo visual */ }
    };

    // ===================== BÚSQUEDA POR NÚMERO =====================
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [highlightValue, setHighlightValue] = useState(null);
    const [highlightTick, setHighlightTick] = useState(0);

    const handleSearch = useCallback((e) => {
        e?.preventDefault?.();
        const q = String(searchValue || '').trim();
        if (!q) return;

        // Buscar dentro del conjunto filtrado por empresa
        const idx = filteredData.findIndex(it => String(it.manifiesto) === q);
        if (idx === -1) {
            const msg = companyFilter
                ? `No se encontró el manifiesto ${q} para la empresa seleccionada.`
                : `No se encontró el manifiesto ${q}.`;
            toast.error(msg, { toastId: 'mf-not-found', autoClose: 3500, closeOnClick: true });
            setHighlightValue(null);
            return;
        }

        const newPage = Math.floor(idx / ITEMS_PER_PAGE) + 1;
        setPage(newPage);
        setHighlightValue(q);
        setHighlightTick(t => t + 1); // fuerza el resaltado

        try {
            const url = new URL(window.location.href);
            url.searchParams.set('manifiesto', q);
            window.history.replaceState({}, '', url.toString());
        } catch { /* Ignorar el error - La actualización de la URL es sólo visual */  }
    }, [searchValue, filteredData, companyFilter]); 

    // Header: Select de empresa + buscador
    const headerRight = (
        <>
            <div style={{ minWidth: 260 }}>
                <label htmlFor="filtroEmpresa" className="form-label mb-1 d-block text-end">
                    Empresa
                </label>
                <select
                    id="filtroEmpresa"
                    className="form-select"
                    value={companyFilter}
                    onChange={handleCompanyChange}
                >
                    <option value="">Todas las empresas</option>
                    {companies.map((emp) => (
                        <option key={emp} value={emp}>{emp}</option>
                    ))}
                </select>
            </div>

            <form
                className="ms-auto"
                onSubmit={handleSearch}
                style={{ maxWidth: 380 }}
            >
                <label htmlFor="buscarManifiesto" className="form-label mb-1 d-block text-end">
                    Buscar por No. de Manifiesto
                </label>
                <div className="input-group">
                    <input
                        id="buscarManifiesto"
                        type="text"
                        className="form-control"
                        placeholder="Ej: 59387"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <button type="submit" className="btn btn-primary">
                        <span className="material-icons" style={{ verticalAlign: 'middle' }}>search</span> Buscar
                    </button>
                </div>
            </form>
        </>
    );

    if (loading) return <Loading mensaje="Cargando Manifiestos OnRoad..." />;
    if (error) return <div className="text-danger">Error: {error.message}</div>;

    return (
        <div className="container">
            <ToastContainer
                position="top-right"
                autoClose={3500}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
                limit={3}
            />

            <TablaLayout
                title="Manifiestos OnRoad"
                data={filteredData}
                columns={tableColumns}
                keyField={keyField}
                itemsPerPage={ITEMS_PER_PAGE}
                onView={item => openModal('consultar', item)}
                onDelete={remove}
                showConsult={true}
                showCreate={false}
                showEdit={false}
                headerRight={headerRight}
                page={page}
                onPageChange={setPage}
                highlightByKey="manifiesto"
                highlightValue={highlightValue}
                highlightDurationMs={3000}
                highlightTick={highlightTick}
                breadcrumbs={[
                    { label: 'Inicio', href: '/inicio' },
                    { label: 'Manifiestos' }
                ]}
            />

            <ModalDynamic
                show={showModal}
                accion={actionType}
                title="Detalle del Manifiesto"
                columns={modalColumns}
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onSubmit={submit}
                onClose={closeModal}
                loading={loadingModal}
                size="lg"
            />
        </div>
    );
}
